import { useState, useRef, KeyboardEvent } from 'react';
import { Send, Smile, Image, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { EmojiPicker } from '../Chat/EmojiPicker';
import { useAuth } from '../../contexts/AuthContext';
import { mediaService } from '../../services/mediaService';

interface RoomMessageInputProps {
    onSend: (content: string) => void;
    disabled?: boolean;
}

export function RoomMessageInput({ onSend, disabled = false }: RoomMessageInputProps) {
    const [content, setContent] = useState('');
    const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { sk, loginMethod } = useAuth();

    const handleSend = () => {
        const trimmed = content.trim();
        if (!trimmed || disabled || isUploading) return;

        onSend(trimmed);
        setContent('');
        setIsEmojiPickerOpen(false);

        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const val = e.target.value;
        setContent(val);
        e.target.style.height = 'auto';
        e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
    };

    const handleEmojiSelect = (emoji: string) => {
        if (!textareaRef.current) return;
        const start = textareaRef.current.selectionStart;
        const end = textareaRef.current.selectionEnd;
        const newContent = content.substring(0, start) + emoji + content.substring(end);
        setContent(newContent);
        textareaRef.current.focus();
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !loginMethod) return;

        setIsUploading(true);
        try {
            const url = await mediaService.uploadFile(file, loginMethod, sk || undefined);
            onSend(url);
        } catch (error) {
            console.error('Upload failed:', error);
            alert('Resim yüklenemedi.');
        } finally {
            setIsUploading(false);
            e.target.value = '';
        }
    };

    return (
        <div className="p-4 bg-white dark:bg-surface-900 border-t border-surface-200 dark:border-surface-700 relative">
            <div className="flex items-end gap-3 max-w-4xl mx-auto">
                <div className="flex-1 relative flex items-end bg-surface-50 dark:bg-surface-800 rounded-2xl border border-surface-200 dark:border-surface-700 focus-within:ring-2 focus-within:ring-mavi-500/50 transition-all">
                    <textarea
                        ref={textareaRef}
                        value={content}
                        onChange={handleInput}
                        onKeyDown={handleKeyDown}
                        placeholder="Bir şeyler yaz..."
                        disabled={disabled}
                        rows={1}
                        className="flex-1 px-4 py-3 bg-transparent font-merriweather text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 resize-none focus:outline-none"
                    />

                    <div className="flex items-center pr-2 pb-2 gap-1">
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleImageUpload}
                            accept="image/*"
                            className="hidden"
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={disabled || isUploading}
                            className="p-2 text-gray-400 hover:text-mavi-500 transition-colors"
                        >
                            {isUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Image className="w-5 h-5" />}
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsEmojiPickerOpen(!isEmojiPickerOpen)}
                            disabled={disabled}
                            className="p-2 text-gray-400 hover:text-mavi-500 transition-colors"
                        >
                            <Smile className="w-5 h-5" />
                        </button>
                    </div>

                    <EmojiPicker
                        isOpen={isEmojiPickerOpen}
                        onClose={() => setIsEmojiPickerOpen(false)}
                        onSelect={handleEmojiSelect}
                    />
                </div>

                <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={handleSend}
                    disabled={!content.trim() || disabled || isUploading}
                    className="flex-shrink-0 w-12 h-12 bg-mavi-600 text-white rounded-2xl shadow-lg shadow-mavi-500/20 flex items-center justify-center hover:bg-mavi-700 transition-all disabled:opacity-50"
                >
                    <Send className="w-5 h-5" />
                </motion.button>
            </div>
        </div>
    );
}
